import { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  ArrowRight,
  Building2,
  Calculator,
  Car,
  ChevronDown,
  FerrisWheel,
  Landmark,
  Plus,
  RotateCcw,
  Search,
  SlidersHorizontal,
  Telescope,
} from "lucide-react";
import {
  formatPackagePrice,
  getAboutContent,
  getExpandedAboutContent,
  getPackageHero,
  DOMESTIC_PACKAGE_LINKS,
  NEARBY_PACKAGE_LINKS,
  slugifyPlace,
} from "./packages";
import "./Packages.css";

const DEFAULT_FILTERS = {
  sort: "",
  packageType: "",
  price: "",
  duration: "",
  flight: "",
  theme: "",
};

const FILTER_OPTIONS = {
  sort: [
    { value: "", label: "Sort By" },
    { value: "price-low", label: "Price Low to High" },
    { value: "price-high", label: "Price High to Low" },
    { value: "duration-short", label: "Shortest Duration" },
  ],
  price: [
    { value: "", label: "Price" },
    { value: "under-25000", label: "Under ₹25,000" },
    { value: "25000-75000", label: "₹25,000 - ₹75,000" },
    { value: "75000-plus", label: "₹75,000+" },
  ],
  duration: [
    { value: "", label: "Duration" },
    { value: "short", label: "1N - 4N" },
    { value: "medium", label: "5N - 7N" },
    { value: "long", label: "8N+" },
  ],
  theme: [
    { value: "", label: "Themes" },
    { value: "beach", label: "Beach" },
    { value: "family", label: "Family" },
    { value: "romantic", label: "Romantic" },
    { value: "luxury", label: "Luxury" },
    { value: "adventure", label: "Adventure" },
    { value: "culture", label: "Culture" },
  ],
};

function getNights(duration = "") {
  const match = String(duration).match(/(\d+)\s*N/i);
  return match ? Number(match[1]) : 0;
}

/*
 * Mirror of the backend's isValidHolidayDepartureDate (holidayBooking.js).
 * The UI prevents invalid dates where practical; the backend remains authoritative.
 */
function isValidDepartureDate(dateValue, pkg) {
  if (!dateValue || !pkg) return false;

  const date = new Date(`${dateValue}T00:00:00`);
  const today = new Date();
  today.setHours(0, 0, 0, 0);

  if (Number.isNaN(date.getTime())) return false;

  if (
    pkg.booking_start_date &&
    dateValue < String(pkg.booking_start_date).slice(0, 10)
  ) {
    return false;
  }

  if (
    pkg.booking_end_date &&
    dateValue > String(pkg.booking_end_date).slice(0, 10)
  ) {
    return false;
  }

  const minimumDate = new Date(today);
  minimumDate.setDate(
    minimumDate.getDate() + Number(pkg.booking_min_advance_days || 0)
  );

  if (date < minimumDate) return false;

  const maxYears = Number(pkg.booking_max_advance_years || 0);

  if (maxYears > 0) {
    const maximumDate = new Date(today);
    maximumDate.setFullYear(maximumDate.getFullYear() + maxYears);

    if (date > maximumDate) return false;
  }

  if (pkg.departure_type === "daily") return true;

  if (pkg.departure_type === "recurring") {
    if (!pkg.departure_start_date) return false;

    const startDate = new Date(
      `${String(pkg.departure_start_date).slice(0, 10)}T00:00:00`
    );
    const interval = Number(pkg.departure_interval_days || 0);

    if (interval < 1 || date < startDate) return false;

    const diffDays = Math.round((date - startDate) / (1000 * 60 * 60 * 24));

    return diffDays % interval === 0;
  }

  if (pkg.departure_type === "fixed") {
    let fixedDates = pkg.departure_dates;

    if (typeof fixedDates === "string") {
      try {
        fixedDates = JSON.parse(fixedDates);
      } catch {
        fixedDates = [];
      }
    }

    if (!Array.isArray(fixedDates)) fixedDates = [];

    return fixedDates
      .map((d) => String(d).slice(0, 10))
      .includes(dateValue);
  }

  return false;
}

function packageMatchesTheme(pkg, theme) {
  if (!theme) return true;
  const searchable = [
    pkg.title,
    pkg.groupType,
    pkg.location,
    pkg.airline,
    ...(pkg.highlights || []),
  ].join(" ").toLowerCase();
  const aliases = {
    beach: ["beach", "island", "coastal", "sunset"],
    family: ["family", "kid", "breakfast", "transfers"],
    romantic: ["romantic", "couple", "honeymoon", "private"],
    luxury: ["luxury", "premium", "5 star", "resort"],
    adventure: ["adventure", "snow", "excursion", "trail"],
    culture: ["culture", "city tour", "old town", "temple", "heritage"],
  };
  return (aliases[theme] || [theme]).some((word) => searchable.includes(word));
}

export default function Packages() {
  const navigate = useNavigate();
  const { place = "" } = useParams();

  const placeSlug = slugifyPlace(place);

  const hero = useMemo(
    () => getPackageHero(placeSlug),
    [placeSlug]
  );

  const about = useMemo(
    () => getAboutContent(placeSlug),
    [placeSlug]
  );

  const expandedAbout = useMemo(
    () => getExpandedAboutContent(placeSlug),
    [placeSlug]
  );

  const [packages, setPackages] = useState([]);
  const [packagesLoading, setPackagesLoading] = useState(true);
  const [packagesError, setPackagesError] = useState("");

  const [from, setFrom] = useState("New Delhi");
  const [to, setTo] = useState(hero.destination);
  const [showFullAbout, setShowFullAbout] = useState(false);
  const [showFilters, setShowFilters] = useState(true);
  const [filters, setFilters] = useState(DEFAULT_FILTERS);
  const [bookingPackage, setBookingPackage] = useState(null);
  const [travelDate, setTravelDate] = useState("");
  const [travelDateError, setTravelDateError] = useState("");
  const [showRoomForm, setShowRoomForm] = useState(false);
  const [rooms, setRooms] = useState([{ adults: 2, children: 0 }]);

    useEffect(() => {
    let cancelled = false;

    async function loadPackages() {
      setPackagesLoading(true);
      setPackagesError("");

      try {
        const API_BASE =
  (
    typeof process !== "undefined" && process.env?.REACT_APP_API_URL
      ? process.env.REACT_APP_API_URL
      : "http://localhost:5000"
  ).replace(/\/$/, "");

        const query = new URLSearchParams();

        if (place) {
          query.set("place", place);
        }

        const response = await fetch(
          `${API_BASE}/api/packages?${query.toString()}`
        );

        if (!response.ok) {
          throw new Error(`Failed to load packages: ${response.status}`);
        }

        const data = await response.json();

        if (cancelled) return;

        const packageList = Array.isArray(data)
          ? data
          : Array.isArray(data.data)
            ? data.data
            : [];

        setPackages(packageList);
      } catch (error) {
        if (cancelled) return;

        console.error("Failed to load holiday packages:", error);
        setPackages([]);
        setPackagesError("Unable to load packages.");
      } finally {
        if (!cancelled) {
          setPackagesLoading(false);
        }
      }
    }

    loadPackages();

    return () => {
      cancelled = true;
    };
  }, [place]);


  const packageTypeOptions = useMemo(() => {
    const types = [...new Set(packages.map((pkg) => pkg.groupType))];
    return [{ value: "", label: "Package Type" }, ...types.map((type) => ({ value: type, label: type }))];
  }, [packages]);

  const flightOptions = useMemo(() => {
    const flights = [...new Set(packages.map((pkg) => pkg.airline))];
    return [{ value: "", label: "Flight" }, ...flights.map((flight) => ({ value: flight, label: flight }))];
  }, [packages]);

  const filteredPackages = useMemo(() => {
    const nextPackages = packages.filter((pkg) => {
      const nights = getNights(pkg.duration);
      const packagePrice = Number(pkg.final_price) || 0;

const priceMatch =
  !filters.price ||
  (filters.price === "under-25000" && packagePrice < 25000) ||
  (filters.price === "25000-75000" &&
    packagePrice >= 25000 &&
    packagePrice <= 75000) ||
  (filters.price === "75000-plus" && packagePrice > 75000);
      const durationMatch =
        !filters.duration ||
        (filters.duration === "short" && nights <= 4) ||
        (filters.duration === "medium" && nights >= 5 && nights <= 7) ||
        (filters.duration === "long" && nights >= 8);

      return (
        (!filters.packageType || pkg.groupType === filters.packageType) &&
        (!filters.flight || pkg.airline === filters.flight) &&
        priceMatch &&
        durationMatch &&
        packageMatchesTheme(pkg, filters.theme)
      );
    });

    return [...nextPackages].sort((first, second) => {
  const firstPrice = Number(first.final_price) || 0;
  const secondPrice = Number(second.final_price) || 0;

  if (filters.sort === "price-low") {
    return firstPrice - secondPrice;
  }

  if (filters.sort === "price-high") {
    return secondPrice - firstPrice;
  }

  if (filters.sort === "duration-short") {
    return getNights(first.duration) - getNights(second.duration);
  }

  return 0;
});
  }, [filters, packages]);

  const updateFilter = (name, value) => {
    setFilters((current) => ({ ...current, [name]: value }));
  };

  const handleSearch = () => {
    const nextPlace = slugifyPlace(to || hero.destination);
    if (nextPlace) navigate(`/holidays/packages/${nextPlace}`);
  };

  const openBookingCalendar = (pkg) => {
    setBookingPackage(pkg);
    setTravelDate("");
    setTravelDateError("");
    setShowRoomForm(false);
    setRooms([{ adults: 2, children: 0 }]);
  };

  const continueToBooking = () => {
  if (!bookingPackage?.id || !travelDate) return;

  const packagePlace =
    bookingPackage.destination ||
    bookingPackage.place ||
    bookingPackage.location ||
    hero.destination;

  if (!packagePlace || !bookingPackage.title) return;

  const totalAdults = rooms.reduce(
    (total, room) => total + (Number(room.adults) || 0),
    0
  );

  const totalChildren = rooms.reduce(
    (total, room) => total + (Number(room.children) || 0),
    0
  );

  const totalTravellers = totalAdults + totalChildren;

  if (totalAdults < 1 || totalTravellers < 1) return;

  navigate(
    `/holidays/booking/${slugifyPlace(packagePlace)}/${slugifyPlace(
      bookingPackage.title
    )}`,
    {
      state: {
        packageId: bookingPackage.id,
        package: bookingPackage,
        from,
        destination: packagePlace,
        travelDate,
        rooms,
        travellers: totalTravellers,
        adults: totalAdults,
        children: totalChildren,
        pricingBasis: bookingPackage.pricing_basis || "per_person",
      },
    }
  );
};

  const updateRoom = (index, key, delta) => {
    setRooms((current) => current.map((room, roomIndex) => {
      if (roomIndex !== index) return room;
      const min = key === "adults" ? 1 : 0;
      return { ...room, [key]: Math.max(min, room[key] + delta) };
    }));
  };

  const addRoom = () => {
    setRooms((current) => [...current, { adults: 2, children: 0 }]);
  };

  const removeRoom = (index) => {
    setRooms((current) => current.length === 1 ? current : current.filter((_, roomIndex) => roomIndex !== index));
  };

  return (
    <main className="packages-page">
      <section className="packages-hero">
        <div className="packages-hero-collage" aria-hidden="true">
          {hero.images.map((image, index) => (
            <span key={`${image}-${index}`}>
              <img src={image} alt="" />
            </span>
          ))}
        </div>
        <div className="packages-hero-shade" />

        <div className="packages-hero-content">
          <h1>{hero.title}</h1>
          <div className="packages-search">
            <div className="packages-field packages-field-from">
              <Search size={25} />
              <label>
                <span>From</span>
                <input value={from} onChange={(e) => setFrom(e.target.value)} />
              </label>
            </div>
            <div className="packages-field">
              <label>
                <span>To Destination/Category</span>
                <input value={to} onChange={(e) => setTo(e.target.value)} />
              </label>
              <ChevronDown size={18} />
            </div>
            <button type="button" onClick={handleSearch}>Search</button>
          </div>
        </div>
      </section>

      <section className="packages-results">
        <div className="packages-results-head">
          <div>
            <p>Curated Holiday Packages</p>
            <h2>{hero.destination} packages from {from}</h2>
          </div>
          <button type="button" onClick={() => navigate("/holidays")}>Back to Holidays</button>
        </div>

        <div className="packages-filter-top">
          <button type="button" onClick={() => setShowFilters((current) => !current)}>
            <SlidersHorizontal size={15} />
            {showFilters ? "Hide Filters" : "Show Filters"}
          </button>
        </div>

        {showFilters && (
          <div className="packages-filter-bar">
            {[
              ["sort", FILTER_OPTIONS.sort],
              ["packageType", packageTypeOptions],
              ["price", FILTER_OPTIONS.price],
              ["duration", FILTER_OPTIONS.duration],
              ["flight", flightOptions],
              ["theme", FILTER_OPTIONS.theme],
            ].map(([name, options]) => (
              <label className="packages-filter-select" key={name}>
                <select value={filters[name]} onChange={(event) => updateFilter(name, event.target.value)}>
                  {options.map((option) => (
                    <option value={option.value} key={option.value || option.label}>
                      {option.label}
                    </option>
                  ))}
                </select>
                <ChevronDown size={15} />
              </label>
            ))}
            <button className="packages-reset" type="button" onClick={() => setFilters(DEFAULT_FILTERS)}>
              <RotateCcw size={14} />
              Reset All
            </button>
          </div>
        )}

         {packagesLoading ? (
  <p className="packages-empty">
    Loading holiday packages...
  </p>
) : packagesError ? (
  <p className="packages-empty">
    {packagesError}
  </p>
) : (
  <>
    <div className="packages-card-grid">
      {filteredPackages.map((pkg) => (
        <article className="packages-card" key={pkg.id}>
          <div className="packages-card-media">
            <img
              src={pkg.image_url}
              alt={pkg.title || "Holiday package"}
            />

            <label className="packages-compare">
              <input type="checkbox" />
              Add to Compare
            </label>

            <span className="packages-airline">
              {pkg.airline}
            </span>
          </div>

          <div className="packages-card-body">
            <h3>{pkg.title}</h3>

            <p className="packages-route">
              {pkg.location}
            </p>

            <div className="packages-icons">
              <Building2 />
              <Telescope />
              <Car />
              <FerrisWheel />
              <Landmark />
            </div>

            <ul className="packages-highlights">
              {(pkg.highlights || []).map((highlight) => (
                <li key={highlight}>{highlight}</li>
              ))}
            </ul>
          </div>

          <div className="packages-card-footer">
  <div>
    <span>Starting From</span>

    <strong>
      {Number(pkg.discount_percent) > 0 && (
        <del>{formatPackagePrice(Number(pkg.base_price) || 0)}</del>
      )}{" "}
      {formatPackagePrice(Number(pkg.selling_price) || 0)}
    </strong>

    {Number(pkg.discount_percent) > 0 && (
      <p>{Number(pkg.discount_percent)}% OFF</p>
    )}

    <p>
      {pkg.pricing_basis === "per_room"
        ? "Per Room"
        : pkg.pricing_basis === "per_booking"
          ? "Per Booking"
          : "Per Person"}
    </p>

    <p>
      GST: {formatPackagePrice(Number(pkg.gst) || 0)} ·
      Other Tax: {formatPackagePrice(Number(pkg.other_tax) || 0)} ·
      Service Fee: {formatPackagePrice(Number(pkg.service_fee) || 0)}
    </p>

    <p>
      <strong>
        Total to Pay: {formatPackagePrice(Number(pkg.final_price) || 0)}
      </strong>
    </p>
  </div>

            <button
              type="button"
              onClick={() => openBookingCalendar(pkg)}
            >
              Book Now <ArrowRight size={16} />
            </button>
          </div>
        </article>
      ))}
    </div>

    {!filteredPackages.length && (
      <p className="packages-empty">
        No published packages found for this destination or filter.
      </p>
    )}
  </>
)}

        <section className="packages-about-section">
          <h2>{about.title}</h2>
          {about.paragraphs.map((paragraph) => (
            <p key={paragraph}>{paragraph}</p>
          ))}
          <h3>{about.subTitle}</h3>
          <p>{about.subText}</p>
          <button type="button" onClick={() => setShowFullAbout((current) => !current)}>
            {showFullAbout ? "Read Less" : "Read More"}
          </button>

          {showFullAbout && (
            <div className="packages-expanded-about">
              {expandedAbout.intro.map((paragraph) => (
                <p key={paragraph}>{paragraph}</p>
              ))}

              <ul className="packages-about-list">
                {expandedAbout.travellerTypes.map((item) => (
                  <li key={item.title}>
                    <strong>{item.title}:</strong> {item.text}
                  </li>
                ))}
              </ul>

              <h3>{expandedAbout.placesTitle}</h3>
              {expandedAbout.placesIntro.map((paragraph) => (
                <p key={paragraph}>{paragraph}</p>
              ))}
              <ul className="packages-place-list">
                {expandedAbout.topPlaces.map((item) => (
                  <li key={item.name}>
                    <strong>{item.name}</strong>
                    <p>{item.text}</p>
                  </li>
                ))}
              </ul>

              <h3>{expandedAbout.shoppingTitle}</h3>
              <p>{expandedAbout.shoppingText}</p>
              <ul className="packages-about-list">
                {expandedAbout.shoppingAreas.map((area) => (
                  <li key={area}>
                    <strong>{area}:</strong> Popular for souvenirs, local products, daily essentials and relaxed shopping.
                  </li>
                ))}
              </ul>

              <h3>{expandedAbout.foodTitle}</h3>
              <p>{expandedAbout.foodText}</p>
              <table className="packages-food-table">
                <thead>
                  <tr>
                    <th>Vegetarian</th>
                    <th>Non-Vegetarian</th>
                  </tr>
                </thead>
                <tbody>
                  {expandedAbout.foodTable.vegetarian.map((vegItem, index) => (
                    <tr key={vegItem}>
                      <td>{vegItem}</td>
                      <td>{expandedAbout.foodTable.nonVegetarian[index]}</td>
                    </tr>
                  ))}
                </tbody>
              </table>

              <h3>{expandedAbout.bookingTitle}</h3>
              <p>{expandedAbout.bookingText}</p>
            </div>
          )}
        </section>

        <section className="packages-link-section">
          <h2>Nearby Places to Explore</h2>
          <div className="packages-link-grid">
            {NEARBY_PACKAGE_LINKS.map((placeName) => (
              <button
                type="button"
                key={placeName}
                onClick={() => navigate(`/holidays/packages/${slugifyPlace(placeName)}`)}
              >
                {placeName} Holiday Packages
              </button>
            ))}
          </div>
        </section>

        <section className="packages-link-section">
          <h2>Best Domestic Holidays</h2>
          <div className="packages-link-grid">
            {DOMESTIC_PACKAGE_LINKS.map((placeName) => (
              <button
                type="button"
                key={placeName}
                onClick={() => navigate(`/holidays/packages/${slugifyPlace(placeName)}`)}
              >
                {placeName} Holiday Packages
              </button>
            ))}
          </div>
        </section>
      </section>

      {bookingPackage && (
        <div className="packages-calendar-overlay" onClick={(event) => event.target === event.currentTarget && setBookingPackage(null)}>
          <section className="packages-calendar-modal" role="dialog" aria-modal="true" aria-label="Select travel date">
            <button className="packages-calendar-close" type="button" onClick={() => setBookingPackage(null)}>×</button>
            <p>Choose Travel Date</p>
            <h2>{bookingPackage.title}</h2>
            <span>{bookingPackage.duration} | {bookingPackage.groupType}</span>
            <label>
              <small>Departure Date</small>
              <input
                type="date"
                value={travelDate}
                min={(() => {
                  const today = new Date();
                  today.setDate(
                    today.getDate() + Number(bookingPackage.booking_min_advance_days || 0)
                  );

                  return today.toISOString().split("T")[0];
                })()}
                max={(() => {
                  const today = new Date();
                  today.setFullYear(
                    today.getFullYear() + Number(bookingPackage.booking_max_advance_years || 2)
                  );

                  return today.toISOString().split("T")[0];
                })()}
                onChange={(event) => {
                  const value = event.target.value;

                  if (value && !isValidDepartureDate(value, bookingPackage)) {
                    setTravelDateError(
                      "Selected date is not an available departure date for this package."
                    );
                    setTravelDate("");
                    return;
                  }

                  setTravelDateError("");
                  setTravelDate(value);
                }}
              />
            </label>
            {travelDateError && (
              <p className="packages-date-error" role="alert">{travelDateError}</p>
            )}
            <div className="packages-calendar-actions">
              <button type="button" onClick={() => setBookingPackage(null)}>Cancel</button>
              <button type="button" disabled={!travelDate} onClick={() => setShowRoomForm(true)}>
                Continue <ArrowRight size={15} />
              </button>
            </div>
          </section>
        </div>
      )}

      {bookingPackage && showRoomForm && (
        <div className="packages-room-overlay" onClick={(event) => event.target === event.currentTarget && setShowRoomForm(false)}>
          <section className="packages-room-modal" role="dialog" aria-modal="true" aria-label="Select rooms and travellers">
            <button className="packages-room-close" type="button" onClick={() => setShowRoomForm(false)}>×</button>
            <h2>{bookingPackage.title}</h2>
            <p>{bookingPackage.route}</p>
            <div className="packages-room-date">
              <span>Selected Date</span>
              <strong>{travelDate.split("-").reverse().join("-")}</strong>
            </div>
            {rooms.map((room, index) => (
              <div className="packages-room-row" key={`room-${index + 1}`}>
                <div className="packages-room-title">
                  <h3>Room {index + 1}</h3>
                  {rooms.length > 1 && (
                    <button type="button" onClick={() => removeRoom(index)} aria-label={`Remove room ${index + 1}`}>⌫</button>
                  )}
                </div>
                <div className="packages-room-controls">
                  <div>
                    <span>Adult</span>
                    <small>(Above 12 years)</small>
                  </div>
                  <div className="packages-stepper">
                    <button type="button" onClick={() => updateRoom(index, "adults", -1)}>−</button>
                    <strong>{room.adults}</strong>
                    <button type="button" onClick={() => updateRoom(index, "adults", 1)}>+</button>
                  </div>
                  <div>
                    <span>Child <b>ⓘ</b></span>
                    <small>(Below 12 years)</small>
                  </div>
                  <div className="packages-stepper">
                    <button type="button" onClick={() => updateRoom(index, "children", -1)}>−</button>
                    <strong>{room.children}</strong>
                    <button type="button" onClick={() => updateRoom(index, "children", 1)}>+</button>
                  </div>
                </div>
              </div>
            ))}
            <button className="packages-add-room" type="button" onClick={addRoom}>
              <Plus size={16} />
              Add Room
            </button>
            <button className="packages-calc-btn" type="button" onClick={continueToBooking}>
              <Calculator size={16} />
Continue to Booking
            </button>
          </section>
        </div>
      )}
    </main>
  );
}